import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  TextField,
  Button,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import FooterLinks from './FooterLinks';
import { createDefaultPages } from '../../services/pageService';

const NewStoreFooter: React.FC = () => {
  const { settings, loading: storeLoading } = useStoreSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // إنشاء الصفحات الافتراضية عند تحميل المكون
  useEffect(() => {
    const initializeDefaultPages = async () => {
      try {
        await createDefaultPages();
      } catch (error) {
        console.error('Error initializing default pages:', error);
      }
    };

    initializeDefaultPages();
  }, []);

  // استخراج بيانات المتجر من الإعدادات
  const storeName = settings?.general?.storeName || 'اليكترونيات السواح';
  const storeEmail = settings?.general?.storeEmail || 'info@alsawah-electronics.com';
  const storePhone = settings?.general?.storePhone || '0123456789';
  const storeAddress = settings?.general?.storeAddress || 'العنوان، المدينة، البلد';
  const workingHours = settings?.general?.workingHours;

  // روابط سريعة
  const quickLinks = [
    { title: 'الرئيسية', url: '/' },
    { title: 'المنتجات', url: '/products' },
    { title: 'العروض', url: '/offers' },
    { title: 'من نحن', url: '/about' },
    { title: 'اتصل بنا', url: '/contact' },
    { title: 'الأسئلة الشائعة', url: '/faq' }
  ];

  // روابط المعلومات
  const informationLinks = [
    { title: 'سياسة الخصوصية', url: '/privacy-policy' },
    { title: 'شروط الاستخدام', url: '/terms' },
    { title: 'سياسة الإرجاع', url: '/return-policy' },
    { title: 'طرق الدفع', url: '/payment-methods' },
    { title: 'الشحن والتوصيل', url: '/shipping' }
  ];

  // روابط الفئات
  const categoryLinks = [
    { title: 'الهواتف الذكية', url: '/category/1' },
    { title: 'الحواسيب', url: '/category/2' },
    { title: 'الإكسسوارات', url: '/category/3' },
    { title: 'الساعات الذكية', url: '/category/4' },
    { title: 'التلفزيونات', url: '/category/5' }
  ];

  // معلومات الاتصال - استخدام بيانات من إعدادات المتجر
  const contactInfo = {
    phone: storePhone,
    email: storeEmail,
    address: storeAddress
  };

  // وسائل التواصل الاجتماعي
  const socialLinks = [
    { icon: FacebookIcon, url: 'https://facebook.com' },
    { icon: TwitterIcon, url: 'https://twitter.com' },
    { icon: InstagramIcon, url: 'https://instagram.com' },
    { icon: YouTubeIcon, url: 'https://youtube.com' },
    { icon: LinkedInIcon, url: 'https://linkedin.com' }
  ];

  // إذا كانت البيانات لا تزال تحمل، عرض Loading
  if (storeLoading) {
    return (
      <Box
        component="footer"
        sx={{
          bgcolor: 'background.paper',
          pt: 6,
          pb: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="footer" sx={{
        bgcolor: 'background.paper',
        pt: 6,
        pb: 3,
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)'
      }}>
      {/* القسم الرئيسي */}
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
        <Grid container spacing={4}>
          {/* معلومات المتجر */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                {storeName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                نحن نسعى دائمًا لتقديم أفضل المنتجات بأفضل الأسعار مع خدمة عملاء متميزة. ثقتكم هي أولويتنا.
              </Typography>

              {/* وسائل التواصل الاجتماعي */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <IconButton
                      key={index}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'white',
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }}
                      size="small"
                    >
                      <Icon fontSize="small" />
                    </IconButton>
                  );
                })}
              </Box>

              {/* معلومات الاتصال */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {contactInfo.phone}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {contactInfo.email}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <LocationIcon fontSize="small" sx={{ mr: 1, mt: 0.5, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {contactInfo.address}
                  </Typography>
                </Box>

                {/* ساعات العمل */}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  ساعات العمل
                </Typography>
                {workingHours ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      السبت: {workingHours.saturday || '9:00 صباحًا - 9:00 مساءً'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      الأحد: {workingHours.sunday || '9:00 صباحًا - 9:00 مساءً'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      الإثنين: {workingHours.monday || '9:00 صباحًا - 9:00 مساءً'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      الثلاثاء: {workingHours.tuesday || '9:00 صباحًا - 9:00 مساءً'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      الأربعاء: {workingHours.wednesday || '9:00 صباحًا - 9:00 مساءً'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      الخميس: {workingHours.thursday || '9:00 صباحًا - 9:00 مساءً'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      الجمعة: {workingHours.friday || '2:00 مساءً - 9:00 مساءً'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      السبت - الخميس: 9:00 صباحًا - 9:00 مساءً
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      الجمعة: 2:00 مساءً - 9:00 مساءً
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Grid>

          {/* روابط سريعة */}
          <Grid item xs={6} sm={4} md={2}>
            <FooterLinks section="quick_links" title="روابط سريعة" />
          </Grid>

          {/* روابط المعلومات */}
          <Grid item xs={6} sm={4} md={2}>
            <FooterLinks section="information" title="معلومات" />
          </Grid>

          {/* روابط الفئات */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
              الفئات
            </Typography>
            <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
              {categoryLinks.map((link, index) => (
                <Box component="li" key={index} sx={{ mb: 1 }}>
                  <Link
                    href={link.url}
                    underline="hover"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'translateX(-8px)'
                      }
                    }}
                  >
                    {link.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* النشرة البريدية */}
          <Grid item xs={12} sm={8} md={2}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
              النشرة البريدية
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              اشترك في نشرتنا البريدية للحصول على آخر العروض والتحديثات.
            </Typography>

            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                placeholder="البريد الإلكتروني"
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px'
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                sx={{ fontWeight: 'bold' }}
              >
                اشترك الآن
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* شعارات الدفع */}
        <Box sx={{ mt: 5, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
            وسائل الدفع المتاحة
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {['visa', 'mastercard', 'mada', 'applepay', 'stcpay'].map((payment) => (
              <Paper
                key={payment}
                elevation={0}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#f5f5f5',
                  width: 60,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  {payment}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      </Container>

      {/* حقوق النشر */}
      <Box sx={{
        bgcolor: '#f5f5f5',
        py: 2,
        mt: 3,
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)'
      }}>
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1, sm: 0 } }}>
              &copy; {new Date().getFullYear()} {storeName}. جميع الحقوق محفوظة.
            </Typography>

            <Typography variant="body2" color="text.secondary">
              تم التطوير بواسطة <Link href="#" underline="hover" color="primary.main">شركة البرمجة</Link>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default NewStoreFooter;
